/**
 * Pure cap-validation helpers for the workspace's per-field validation effect.
 *
 * Separated from the React effect so the client cap math can be unit-tested
 * without mounting the full workspace. Server mirrors the same rules in
 * validateLenderCreditCap / validateLocrEligibility — any drift here escapes
 * the hook tests that mock these gates, so keep the rules in lockstep.
 */

import { formatCurrency } from './format';

// FP absorption: maxGenericLenderCreditDollars is derived via
// availableLcPts × loanAmount × 0.01 − lenderPaidCC, which accumulates enough
// rounding that a value numerically equal to the cap can read as slightly over.
const CAP_EPSILON = 0.005;

/**
 * Check whether a user-entered lender credit dollar amount falls within the
 * generic-lender-credit cap. Returns a user-facing invalid message when the
 * requested amount exceeds the cap, or null otherwise.
 *
 * `dirty` gates the cap check — a field that's never been touched by the user
 * should not be marked invalid even if the loan's existing value somehow
 * exceeds a freshly-computed cap (rare, but possible when the waterfall shifts
 * outside the save-group).
 */
export function checkLenderCreditCap(
  requestedDollars: number,
  maxGenericLenderCreditDollars: number,
  dirty: boolean
): string | null {
  if (!dirty) {
return null;
}
  if (!Number.isFinite(requestedDollars) || requestedDollars < 0) {
return null;
} // basic-numeric gate owns these
  if (requestedDollars > maxGenericLenderCreditDollars + CAP_EPSILON) {
    return `Exceeds max generic lender credit of ${formatCurrency(maxGenericLenderCreditDollars)}`;
  }
  return null;
}

/**
 * Check whether a user-entered LOCR bps value is within the built-in LO
 * compensation. Returns a user-facing invalid message when over-cap, or null
 * otherwise. When `builtInLoCompBps === 0`, LOCR is unavailable and eligibility
 * (not this function) handles the disable; this cap check short-circuits to
 * null so we don't layer a misleading "exceeds 0 bps" message on top.
 */
export function checkLocrCap(
  requestedBps: number,
  builtInLoCompBps: number,
  dirty: boolean
): string | null {
  if (!dirty) {
return null;
}
  if (!Number.isFinite(requestedBps) || requestedBps < 0) {
return null;
}
  if (builtInLoCompBps <= 0) {
return null;
}
  if (requestedBps > builtInLoCompBps) {
    return `Exceeds built-in LO compensation of ${builtInLoCompBps} bps`;
  }
  return null;
}
